/* eslint-disable no-unused-expressions */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import kconvert from "k-convert";
import { MapPin } from "react-feather";
import { useHistory } from "react-router-dom";

import { getDistance } from "../util/functions";
import { useLocation } from "../Provider/LocationProvider";
import { useRegion } from "../Provider/RegionProvider";
import { getCurrency } from "../static/post";
/* =============================================================================
<PostCard />
============================================================================= */
const PostCard = ({ item = {}, onPress, children }) => {
  const navigation = useHistory();
  const [distance, setDistance] = useState(null);
  const { location } = useLocation();
  const { country } = useRegion();
  const _handleVehicleDetail = useCallback(() => {
    if (typeof onPress === "function") {
      onPress();
    } else {
      navigation.push(`/post/${item?.id}`);
    }
  }, [onPress, navigation, item?.id]);

  useEffect(() => {
    let cancelled = false;

    const shouldCompute =
      item?.location?.lat != null &&
      item?.location?.lng != null &&
      location?.lat != null &&
      location?.lng != null &&
      (item?.miles == null || item?.miles === "");

    if (!shouldCompute) {
      setDistance(item?.miles ?? null);
      return undefined;
    }

    const compute = async () => {
      try {
        const d = await getDistance(
          item.location.lat,
          item.location.lng,
          location,
        );
        if (!cancelled) {
          setDistance(d);
        }
      } catch (err) {
        // console.warn('distance failed', err);
        if (!cancelled) setDistance(null);
      }
    };

    compute();

    return () => {
      cancelled = true;
    };
  }, [item?.location?.lat, item?.location?.lng, location]);

  const postTitle = useMemo(() => {
    const year = item?.year ? `${item.year} ` : "";
    const make = item?.make || item?.titleVehicle || "";
    const model = item?.model || "";
    return `${year}${make} ${model}`.trim();
  }, [item?.year, item?.make, item?.titleVehicle, item?.model]);
  const distanceUnit = country.code === "US" ? "m." : "K";
  const formattedMiles = useMemo(() => {
    const m = item?.miles ?? distance;
    if (m == null) return null;
    try {
      return `${kconvert.convertTo(String(m))} ${distanceUnit}`;
    } catch {
      return `${m} ${distanceUnit}`;
    }
  }, [item?.miles, distance, country.code]);

  const formattedPrice = useMemo(() => {
    if (item?.price == null) return "";
    if(!item.currency?.symbol){
      return  getCurrency(country.code)[0].symbol + " " + item.price;
    }
    return `${item?.currency?.symbol} ${item.price}`;
  }, [item?.price, country.code]);

  return (
    <button
      type="button"
      onClick={_handleVehicleDetail}
      className="rounded-xl relative overflow-hidden bg-gradient-to-b from-gradiantColor1 to-gradiantColor2"
    >
      {Array.isArray(item?.photos) && item.photos.length > 0 && (
        <img
          src={item.photos[0]}
          alt={item?.id}
          className="w-full h-44 object-cover md:h-60 rounded-t mb-1"
          loading="lazy"
        />
      )}

      {item?.isFeatured && (
        <div className="absolute top-0 left-0 m-3 rounded-md py-1 flx items-center justify-center px-3 bg-secondary">
          <p className="text-white text-sm ">Top</p>
        </div>
      )}

      {!!children && children}

      <div className="space-y-2 p-3">
        <p className="text-xl text-left font-bold text-white">{postTitle}</p>

        <div className="flex justify-between items-center">
          {formattedMiles ? (
            <p className="text-white text-md text-center">{formattedMiles}</p>
          ) : (
            <div />
          )}

          <p className="text-right text-secondary text-xl font-bold">
            {formattedPrice}
          </p>
        </div>

        {item?.location && (
          <div className="flex items-center space-x-2">
            <MapPin color="#f1f1f1" size={22} />
            <p className="text-sm text-white truncate max-w-full">
              {item.location.zipCode || item.location.filteredAddress}
            </p>
          </div>
        )}
      </div>
    </button>
  );
};

const propsAreEqual = (prevProps, nextProps) => {
  const onPressEqual = prevProps.onPress === nextProps.onPress;

  const prevItem = prevProps.item || {};
  const nextItem = nextProps.item || {};

  const itemEqual =
    prevItem.id === nextItem.id &&
    prevItem.miles === nextItem.miles &&
    prevItem.price === nextItem.price &&
    prevItem.isFeatured === nextItem.isFeatured &&
    prevItem.titleVehicle === nextItem.titleVehicle &&
    ((Array.isArray(prevItem.photos) &&
      Array.isArray(nextItem.photos) &&
      prevItem.photos.length === nextItem.photos.length) ||
      (!prevItem.photos && !nextItem.photos));

  const layoutEqual = prevProps.layout === nextProps.layout;
  const queryKeyEqual =
    String(prevProps.queryKey) === String(nextProps.queryKey);
  const queryRefEqual =
    String(prevProps.queryRef) === String(nextProps.queryRef);

  return (
    onPressEqual && itemEqual && layoutEqual && queryKeyEqual && queryRefEqual
  );
};

/* Export
============================================================================= */
export default React.memo(PostCard, propsAreEqual);
